import React,{useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';

const {FiTarget,FiCheckCircle,FiArrowRight,FiShield,FiLayers,FiAward,FiBarChart2,FiMap}=FiIcons;

function App() {
  const [email,setEmail]=useState('');
  const [company,setCompany]=useState('');
  const [isSubmitting,setIsSubmitting]=useState(false);
  const [submitStatus,setSubmitStatus]=useState('');

  const handleSubmit=async (e)=> {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    // Log form data for debugging
    console.log('Submitting form:',{email,company});

    try {
      // Web3Forms integration with your new API key
      const formData={
        access_key: 'd226f87a-4160-4884-b21f-542d1845f7bc', // Your updated Web3Forms API key
        subject: `New SEO Intelligence Report Request from ${company}`,
        from_name: company,
        email: email,
        message: `
          New SEO Intelligence Report Request
          
          Company: ${company}
          Email: ${email}
          Service: SEO Intelligence Report
          Price: $2,999
          Delivery: 14 days
        `,
      };

      console.log('Sending request to Web3Forms...');

      const response=await fetch('https://api.web3forms.com/submit',{
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      console.log('Response status:',response.status);
      console.log('Response ok:',response.ok);

      if (response.ok) {
        const result=await response.json();
        console.log('Form submission successful:',result);
        setSubmitStatus('success');
        setEmail('');
        setCompany('');
      } else {
        const errorText=await response.text();
        console.error('Form submission failed:',errorText);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:',error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fallback method - direct email redirect
  const handleFallbackSubmit=(e)=> {
    e.preventDefault();
    const subject=encodeURIComponent(`New SEO Intelligence Report Request from ${company}`);
    const body=encodeURIComponent(`
Company: ${company}
Email: ${email}
Service: SEO Intelligence Report
Price: $2,999
Delivery: 14 days

Please contact me about the SEO Intelligence Report.
    `);
    window.location.href=`mailto:howdy@rankandbeyond.com?subject=${subject}&body=${body}`;
  };

  // Test function for debugging
  const testForm=()=> {
    console.log('Testing form functionality...');
    console.log('Email state:',email);
    console.log('Company state:',company);
    console.log('Is submitting:',isSubmitting);
    console.log('Submit status:',submitStatus);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gray-100 py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-8">
            <span className="inline-block bg-gray-200 px-4 py-2 rounded-full text-gray-700 text-sm font-medium mb-6">
              SEO Intelligence Report
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              The Authority Advantage SEO Intelligence Report
            </h1>
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              Own the Search Results — With the Exact Playbook Top Brands Use
            </p>
          </div>

          <div className="bg-white p-8 border border-gray-300 rounded-lg shadow-sm mb-12">
            <div className="text-center mb-6">
              <div className="text-3xl font-bold text-gray-900 mb-2">$2,999</div>
              <div className="text-gray-600">Complete Intelligence Package</div>
              <div className="text-gray-500 text-sm mt-1">Delivered in 14 days</div>
            </div>

            {submitStatus==='success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
                <div className="flex items-center">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600 mr-2" />
                  <span className="text-green-800 font-medium">
                    Thank you! We've received your request and will contact you within 24 hours.
                  </span>
                </div>
              </div>
            )}

            {submitStatus==='error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                <div className="text-red-800 mb-3">
                  There was an error with the automated form. Please use the alternative options below:
                </div>
                <div className="space-y-2">
                  <button
                    onClick={handleFallbackSubmit}
                    className="w-full bg-gray-800 hover:bg-gray-900 text-white px-4 py-2 rounded-md text-sm transition-colors"
                  >
                    Open Email Client
                  </button>
                  <div className="text-xs text-gray-600">
                    Or email us directly at <strong>howdy@rankandbeyond.com</strong>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="email"
                placeholder="Your business email"
                value={email}
                onChange={(e)=> setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500"
                required
                disabled={isSubmitting}
              />
              <input
                type="text"
                placeholder="Company name"
                value={company}
                onChange={(e)=> setCompany(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500"
                required
                disabled={isSubmitting}
              />
              <motion.button
                whileTap={{scale: 0.98}}
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gray-900 hover:bg-black text-white px-6 py-3 rounded-md font-bold text-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Get My SEO Intelligence Report'}
                {!isSubmitting && <SafeIcon icon={FiArrowRight} className="ml-2 inline-block" />}
              </motion.button>
            </form>

            <div className="mt-6 text-center">
              <div className="flex items-center justify-center">
                <SafeIcon icon={FiShield} className="w-4 h-4 text-gray-600 mr-2" />
                <span className="text-sm text-gray-600">14-day delivery • No-risk guarantee</span>
              </div>
            </div>

            {/* Debug button - remove in production */}
            <div className="mt-4 text-center">
              <button
                onClick={testForm}
                className="text-xs text-gray-400 hover:text-gray-600 underline"
                type="button"
              >
                Debug Form (Check Console)
              </button>
            </div>
          </div>

          <div className="text-center text-gray-500 text-sm">
            Trusted by industry leaders from Airbnb,Spotify,TikTok,and Microsoft
          </div>
        </div>
      </section>

      {/* Main Value Proposition */}
      <section className="py-16 border-t border-gray-200">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-lg text-gray-700">
              For $2,999,delivered in just 14 days,you'll get a custom-built SEO and AI Optimization (AIO) strategy that fuses world-class expertise,elite marketing science,and precision data into one powerful roadmap.
            </p>
            <p className="text-lg text-gray-700 mt-6">
              This isn't another generic audit. This is the equivalent of having the minds of the top marketers,SEOs,and AI strategists in the world working on your site — and handing you the exact moves to dominate your niche.
            </p>
          </div>
        </div>
      </section>

      {/* The 5 Intelligence Layers */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              The 5 Intelligence Layers Behind Your Advantage
            </h2>
          </div>

          <div className="space-y-8">
            {/* Layer 1 */}
            <div className="bg-white border border-gray-300 rounded-lg p-6">
              <div className="flex items-start">
                <SafeIcon icon={FiAward} className="w-6 h-6 text-gray-900 mt-1 mr-4" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Layer 1 — World-Class Expertise (Foundational)</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>13+ years running high-performance SEO campaigns for brands like Airbnb,Spotify,TikTok,Groupon,and the United Nations.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Collaborations with Google & Bing engineers,elite data scientists,and industry-leading marketing teams.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Proven strategies that have driven exponential traffic growth,lowered acquisition costs,and maximized ROI.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Layer 2 */}
            <div className="bg-white border border-gray-300 rounded-lg p-6">
              <div className="flex items-start">
                <SafeIcon icon={FiLayers} className="w-6 h-6 text-gray-900 mt-1 mr-4" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Layer 2 — Academic & Intellectual Firepower (Foundational)</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Doctorate in Marketing with deep research in AI,SEO,and consumer behavior.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Insights drawn from 50 of the most influential marketing thinkers of the past 75 years,including Kotler,Cialdini,Godin,Patel,Brinker,and more.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Integrated frameworks: STP,4Ps/7Ps,Brand Equity,Growth Loops,Behavioral Psychology,Storytelling,Martech — all distilled into a custom AI Marketing Scholar trained to think like the greats.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Layer 3 */}
            <div className="bg-white border border-gray-300 rounded-lg p-6">
              <div className="flex items-start">
                <SafeIcon icon={FiBarChart2} className="w-6 h-6 text-gray-900 mt-1 mr-4" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Layer 3 — Your Data,Perfectly Analyzed (Analytical)</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Deep crawl of your site architecture and content.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>UX testing to pinpoint user friction and missed engagement opportunities.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Analysis of SEO performance using enterprise-level analytics tools.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Layer 4 */}
            <div className="bg-white border border-gray-300 rounded-lg p-6">
              <div className="flex items-start">
                <SafeIcon icon={FiTarget} className="w-6 h-6 text-gray-900 mt-1 mr-4" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Layer 4 — Strategic Market Intelligence (Strategic)</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>In-depth study of your competitors' playbooks,search positioning,and backlink ecosystems.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Mapping your target audience's search intent and online behaviors.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Identifying high-impact,underserved opportunities where you can win fast.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Layer 5 */}
            <div className="bg-white border border-gray-300 rounded-lg p-6">
              <div className="flex items-start">
                <SafeIcon icon={FiMap} className="w-6 h-6 text-gray-900 mt-1 mr-4" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Layer 5 — Tactical Execution Roadmap (Tactical)</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Full technical breakdown of site performance and indexation barriers (search engines + LLM discoverability).</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Prioritized to-do list for immediate and long-term SEO/AIO gains.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Step-by-step action plan tailored to your brand,market,and resources.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              What You Get
            </h2>
          </div>

          <div className="space-y-4 text-center">
            <p className="text-lg text-gray-700">
              A robust,ultra-rich marketing intelligence report — built on the combined minds of the world's best marketers,your real market data,and AI-powered analysis.
            </p>
            <p className="text-lg text-gray-700">
              A clear,prioritized roadmap that your team (or ours) can execute immediately.
            </p>
            <p className="text-lg text-gray-700">
              An extreme competitive edge that rivals simply can't replicate.
            </p>
          </div>

          <div className="mt-12 bg-gray-100 p-8 border border-gray-300 rounded-lg text-center">
            <div className="mb-6">
              <div className="text-3xl font-bold text-gray-900 mb-2">$2,999</div>
              <div className="text-gray-700">Delivery: 14 days</div>
              <div className="text-gray-700 mt-1">Format: Executive-ready PDF + tactical spreadsheet to-do list.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section id="order-form" className="py-16 bg-gray-100">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-white p-8 border border-gray-300 rounded-lg shadow-sm">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Get Your SEO Intelligence Report
              </h2>
              <div className="mb-6">
                <div className="text-3xl font-bold text-gray-900 mb-2">$2,999</div>
                <div className="text-gray-600">Complete Intelligence Package</div>
                <div className="text-gray-500 text-sm mt-1">Delivered in 14 days</div>
              </div>
            </div>

            {submitStatus==='success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
                <div className="flex items-center">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600 mr-2" />
                  <span className="text-green-800 font-medium">
                    Thank you! We've received your request and will contact you within 24 hours.
                  </span>
                </div>
              </div>
            )}

            {submitStatus==='error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                <div className="text-red-800 mb-3">
                  There was an error with the automated form. Please use the alternative options below:
                </div>
                <div className="space-y-2">
                  <button
                    onClick={handleFallbackSubmit}
                    className="w-full bg-gray-800 hover:bg-gray-900 text-white px-4 py-2 rounded-md text-sm transition-colors"
                  >
                    Open Email Client
                  </button>
                  <div className="text-xs text-gray-600">
                    Or email us directly at <strong>howdy@rankandbeyond.com</strong>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-4">
              <input
                type="email"
                placeholder="Your business email"
                value={email}
                onChange={(e)=> setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500"
                required
                disabled={isSubmitting}
              />
              <input
                type="text"
                placeholder="Company name"
                value={company}
                onChange={(e)=> setCompany(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500"
                required
                disabled={isSubmitting}
              />
              <motion.button
                whileTap={{scale: 0.98}}
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gray-900 hover:bg-black text-white px-6 py-3 rounded-md font-bold text-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Get My SEO Intelligence Report'}
                {!isSubmitting && <SafeIcon icon={FiArrowRight} className="ml-2 inline-block" />}
              </motion.button>
            </form>

            <div className="mt-6 text-center">
              <div className="flex items-center justify-center">
                <SafeIcon icon={FiShield} className="w-4 h-4 text-gray-600 mr-2" />
                <span className="text-sm text-gray-600">14-day delivery • No-risk guarantee</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h3 className="text-xl font-bold mb-2">Search Layers</h3>
          <p className="text-gray-400 mb-4">
            Elite SEO intelligence for businesses that refuse to settle for average.
          </p>
          <div className="border-t border-gray-700 pt-4">
            <p className="text-gray-500 text-sm">
              © 2024 Search Layers. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;